import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { z } from 'zod';

export const SelectiveFilterStringType = z.enum([
  'equals',
  'contains',
  'starts-with',
]);

export const SelectiveFilterStringTypes = Object.values(
  SelectiveFilterStringType.Values,
);

export const SelectiveFilterMatchingEquals = z.object({
  type: z.intersection(SelectiveFilterStringType, z.literal('equals')),
  value: z.string(),
});

export type SelectiveFilterMatchingEquals = z.infer<
  typeof SelectiveFilterMatchingEquals
>;

export const SelectiveFilterMatchingContains = z.object({
  type: z.intersection(SelectiveFilterStringType, z.literal('contains')),
  value: z.string(),
});

export type SelectiveFilterMatchingContains = z.infer<
  typeof SelectiveFilterMatchingContains
>;

export const SelectiveFilterStartsWith = z.object({
  type: z.intersection(SelectiveFilterStringType, z.literal('starts-with')),
  value: z.string(),
});

export type SelectiveFilterStartsWith = z.infer<
  typeof SelectiveFilterStartsWith
>;

export const SelectiveFilterDateType = z.enum([
  'date-is-before',
  'date-is-after',
  'date-relative-by-days',
]);

export const SelectiveFilterDateTypes = Object.values(
  SelectiveFilterDateType.Values,
);

export const SelectiveFilterDateIsBefore = z.object({
  type: z.intersection(SelectiveFilterDateType, z.literal('date-is-before')),
  date: z.string(),
});

export type SelectiveFilterDateIsBefore = z.infer<
  typeof SelectiveFilterDateIsBefore
>;

export const SelectiveFilterDateIsAfter = z.object({
  type: z.intersection(SelectiveFilterDateType, z.literal('date-is-after')),
  date: z.string(),
});

export type SelectiveFilterDateIsAfter = z.infer<
  typeof SelectiveFilterDateIsAfter
>;

export const SelectiveFilterDateRelativeByDays = z.object({
  type: z.intersection(
    SelectiveFilterDateType,
    z.literal('date-relative-by-days'),
  ),
  days: z.number(),
});

export type SelectiveFilterDateRelativeByDays = z.infer<
  typeof SelectiveFilterDateRelativeByDays
>;

export const SelectiveFilterStringBasedOption = z.union([
  SelectiveFilterMatchingEquals,
  SelectiveFilterMatchingContains,
  SelectiveFilterStartsWith,
]);

export type SelectiveFilterStringBasedOption = z.infer<
  typeof SelectiveFilterStringBasedOption
>;

export const SelectiveFilterDateBasedOption = z.union([
  SelectiveFilterDateIsBefore,
  SelectiveFilterDateIsAfter,
  SelectiveFilterDateRelativeByDays,
]);

export type SelectiveFilterDateBasedOption = z.infer<
  typeof SelectiveFilterDateBasedOption
>;

export const SelectiveFilterMatchingOption = z.union([
  SelectiveFilterStringBasedOption,
  SelectiveFilterDateBasedOption,
]);

export type SelectiveFilterMatchingOption = z.infer<
  typeof SelectiveFilterMatchingOption
>;

export const matchStringForAnyOfTheseConditions = ({
  value,
  options,
  emptyConfig,
}: {
  value: string;
  options: SelectiveFilterStringBasedOption[];
  emptyConfig: boolean;
}): boolean => {
  if (options.length === 0) {
    return emptyConfig;
  }

  return (
    getIndexForMatchingStringForTheseConditions({
      value,
      options,
    }) !== null
  );
};

export const matchStringToCondition = ({
  value,
  option,
}: {
  value: string;
  option: SelectiveFilterStringBasedOption;
}): boolean => {
  switch (option.type) {
    case 'equals':
      return value === option.value;
    case 'contains':
      return value.includes(option.value);
    case 'starts-with':
      return value.startsWith(option.value);
    default:
      throw guardExhaustive(option);
  }
};

export const getIndexForMatchingStringForTheseConditions = ({
  value,
  options,
}: {
  value: string;
  options: SelectiveFilterStringBasedOption[];
}): number | null => {
  const index = options.findIndex((option) => {
    return matchStringToCondition({
      option,
      value,
    });
  });

  return index === -1 ? null : index;
};

export const getAllIndexesForMatchingStringForTheseConditions = ({
  value,
  options,
}: {
  value: string;
  options: SelectiveFilterStringBasedOption[];
}): number[] => {
  return options.reduce((indexes, option, index) => {
    if (matchStringToCondition({ value, option })) {
      indexes.push(index);
    }
    return indexes;
  }, [] as number[]);
};

export const matchDateForAnyOfTheseConditions = ({
  value,
  currentDate,
  options,
  emptyConfig,
}: {
  value: Date;
  currentDate: Date;
  options: SelectiveFilterDateBasedOption[];
  emptyConfig: boolean;
}): boolean => {
  if (options.length === 0) {
    return emptyConfig;
  }

  return options.some((option) => {
    switch (option.type) {
      case 'date-is-before':
        const beforeDate = new Date(option.date);
        beforeDate.setHours(0, 0, 0, 0);
        return value.getTime() < beforeDate.getTime();
      case 'date-is-after':
        const afterDate = new Date(option.date);
        afterDate.setHours(0, 0, 0, 0);
        return value.getTime() >= afterDate.getTime();
      case 'date-relative-by-days':
        return (
          value.getTime() >=
          new Date(
            currentDate.getTime() - option.days * 24 * 60 * 60 * 1000,
          ).getTime()
        );
    }
  });
};
